import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Field, FieldError, FieldLabel } from './ui/field';
import PageHeader from './PageHeader';
import { cn } from '../lib/utils';
import { LICENSE_INFO_FIELDS, parseLicenseInfoText } from '../utils/licenseInfoParser';
import { isProgramLicenseRecord } from '../utils/licenseHistory';
import { toast } from 'react-hot-toast';
// 修改导入方式，使用 api 实例和 ENDPOINTS 常量
import api, { ENDPOINTS } from '../config/api';
import { 
  Settings2, KeyRound, Mail, User, Globe, 
  Server, FileKey, Loader2, CheckCircle2,
  ChevronRight, Copy, Download, HelpCircle, AlertCircle,
  Calendar, CalendarRange, Clock3, Search, X, ChevronDown,
  ClipboardPaste, Sparkles, Check, RotateCcw, ArrowDownToLine
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Portal from './Portal';

// 防抖函数
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 工具提示组件 - 使用 React.memo 优化
const Tooltip = React.memo(({ content }) => (
  <div className="group relative inline-block">
    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
    <div className="invisible group-hover:visible absolute z-10 w-48 p-2 mt-2 text-sm
                  text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100
                  transition-all duration-200 -translate-x-1/2 left-1/2 transform">
      {content}
    </div>
  </div>
));

// 历史信息选择器组件
const HistorySelector = React.memo(({ onSelect, service }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // 获取服务的显示名称
  const getServiceDisplayName = useCallback((serviceId) => {
    const serviceNames = {
      'conf': 'Confluence',
      'jc': 'JIRA Core',
      'jira': 'JIRA Software',
      'jsm': 'JIRA Service Management',
      'crowd': 'Crowd',
      'crucible': 'Crucible',
      'bitbucket': 'Bitbucket',
      'bamboo': 'Bamboo',
      'plugin': '第三方插件'
    };
    return serviceNames[serviceId] || serviceId;
  }, []);

  // 获取所有服务的历史记录
  const fetchHistoryData = useCallback(async () => {
    setLoading(true);
    try {
      // 获取所有服务类型的历史记录
      const services = ['conf', 'jc', 'jira', 'jsm', 'crowd', 'crucible', 'bitbucket', 'bamboo'];
      const allHistory = [];
      
      for (const svc of services) {
        try {
          const response = await api.get(ENDPOINTS.GET_LICENSES(svc));
          const data = response.data;
          
          if (Array.isArray(data)) {
            // 插件记录可能因旧数据或导入数据混在程序服务中，必须按记录内容再次过滤。
            const dataWithService = data
              .map(item => ({
                ...item,
                serviceType: item.service || svc,
                serviceName: getServiceDisplayName(item.service || svc)
              }))
              .filter(isProgramLicenseRecord);
            allHistory.push(...dataWithService);
          }
        } catch (error) {
          // 忽略单个服务的错误，继续获取其他服务
          console.warn(`获取 ${svc} 历史数据失败:`, error);
        }
      }
      
      // 不再去重，保留所有记录（包括不同产品的同名配置）
      setHistoryData(allHistory);
    } catch (error) {
      console.error('获取历史数据失败:', error);
      toast.error('获取历史数据失败');
    } finally {
      setLoading(false);
    }
  }, [getServiceDisplayName]);

  // 防抖搜索
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchLoading(false);
    }, 300),
    []
  );

  // 过滤历史数据
  const filteredHistory = useMemo(() => {
    const programHistory = historyData.filter(isProgramLicenseRecord);
    if (!searchQuery) return programHistory;
    
    const query = searchQuery.toLowerCase();
    return programHistory.filter(item =>
      item.configName?.toLowerCase().includes(query) ||
      item['授权名']?.toLowerCase().includes(query) ||
      item['邮箱']?.toLowerCase().includes(query) ||
      item['访问域名']?.toLowerCase().includes(query) ||
      item['服务器ID']?.toLowerCase().includes(query) ||
      item.serviceName?.toLowerCase().includes(query)  // 支持按产品名称搜索
    );
  }, [historyData, searchQuery]);

  // 处理搜索输入
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value) {
      setSearchLoading(true);
      debouncedSearch(value);
    }
  };

  // 选择历史记录
  const handleSelectHistory = (item) => {
    // 根据历史记录中的许可证类型来设置
    let licenseType = '2'; // 默认 Data Center
    if (item['许可证类型']) {
      const licenseTypeText = item['许可证类型'].toLowerCase();
      if (licenseTypeText.includes('server') && !licenseTypeText.includes('data center')) {
        licenseType = '1'; // Server 许可证
      } else {
        licenseType = '2'; // Data Center 许可证
      }
    }

    onSelect({
      name: item['授权名'] || '',
      mail: item['邮箱'] || '',
      url: item['访问域名'] || '',
      serverid: item['服务器ID'] || '',
      licenseType: licenseType, // 根据历史记录的许可证类型设置
      configName: item.configName ? `${item.configName}_plugin` : '' // 在原配置名后加 _plugin
    });
    setIsOpen(false);
    toast.success('已自动填充历史信息');
  };

  useEffect(() => {
    if (isOpen) {
      fetchHistoryData();
    }
  }, [isOpen, fetchHistoryData]);

  if (service !== 'plugin') return null;

  return (
    <div className="relative">
      <Button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg px-4 py-2 flex items-center justify-between"
      >
        <span className="flex items-center gap-2">
          <Search className="w-4 h-4" />
          从程序记录中选择配置
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-visible"
          >
            {/* 搜索框 */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索配置名称、产品名称、授权名、邮箱等..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  </div>
                )}
              </div>
            </div>

            {/* 列表内容 - 限制最大高度 */}
            <div className="overflow-y-auto" style={{ maxHeight: '12rem' }}>
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                  加载中...
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {historyData.length === 0 ? '暂无历史记录' : '未找到匹配的记录'}
                </div>
              ) : (
                filteredHistory.map((item, index) => (
                  <div
                    key={item.id || index}
                    onClick={() => handleSelectHistory(item)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                  >
                    <div className="flex flex-col gap-2">
                      {/* 这里只展示已记录的程序配置，不混入插件配置名称。 */}
                      <div className="font-medium text-gray-900 text-sm">
                        {item.configName || '未知配置'}
                      </div>
                      
                      {/* 软件名称 - 单独一行，醒目颜色 */}
                      <div className="text-xs font-semibold text-indigo-600">
                        软件：{item.serviceName || '未知产品'}
                      </div>
                      
                      {/* 详细信息 */}
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>授权名: {item['授权名'] || '未设置'}</div>
                        <div>邮箱: {item['邮箱'] || '未设置'}</div>
                        <div>域名: {item['访问域名'] || '未设置'}</div>
                        <div>服务器ID: {item['服务器ID'] || '未设置'}</div>
                        <div className="text-blue-600 font-medium">许可证类型: {item['许可证类型'] || '未设置'}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 关闭按钮 */}
            <div className="p-3 border-t border-gray-100 bg-white rounded-b-lg">
              <Button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2 rounded-md"
              >
                关闭
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

// 修改 AnimatedFormSection 组件 - 简化版本提升性能
const AnimatedFormSection = React.memo(({ children, icon: Icon, label, description, step, tooltip, delay = 0, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
    className={cn('ui-card license-section overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm', className)}
  >
    <div className="license-section__header border-b bg-muted/35 px-6 py-4">
      <div className="license-section__title">
        <span className="section-icon" aria-hidden="true"><Icon /></span>
        <div>
          {step && <small>{step}</small>}
          <h3 className="flex items-center gap-2 font-semibold text-foreground">
            {label}
            {tooltip && <Tooltip content={tooltip} />}
          </h3>
          {description && <p>{description}</p>}
        </div>
      </div>
    </div>
    <div className="license-section__fields flex flex-col gap-5 p-6">
      {children}
    </div>
  </motion.div>
));

// 修改 FormField 组件 - 简化版本提升性能
const FormField = React.memo(({ label, icon: Icon, error, tooltip, ...props }) => (
  <Field data-invalid={Boolean(error)}>
    <FieldLabel>
      {Icon && <Icon />}
      {label}
      {tooltip && <Tooltip content={tooltip} />}
    </FieldLabel>
      <Input
        {...props}
        aria-invalid={Boolean(error)}
      />
      {error && (
        <FieldError className="flex items-center gap-1">
          <AlertCircle data-icon="inline-start" />
          {error}
        </FieldError>
      )}
  </Field>
));

// 新增的选择字段组件 - 简化版本提升性能
const SelectField = React.memo(({ label, icon: Icon, error, ...props }) => (
  <Field data-invalid={Boolean(error)}>
    <FieldLabel>
      {Icon && <Icon />}
      {label}
    </FieldLabel>
      <select
        {...props}
        aria-invalid={Boolean(error)}
        className={cn('ui-input flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', error && 'border-destructive')}
      />
      {error && <FieldError>{error}</FieldError>}
  </Field>
));

// 主组件
const LicenseGenerator = () => {
  // 使用 useMemo 缓存静态数据
  const services = useMemo(() => [
    { id: 'conf', name: 'Confluence' },
    { id: 'jc', name: 'JIRA Core' },
    { id: 'jira', name: 'JIRA Software' },
    { id: 'jsm', name: 'JIRA Service Management' },
    { id: 'crowd', name: 'Crowd' },
    { id: 'crucible', name: 'Crucible' },
    { id: 'bitbucket', name: 'Bitbucket' },
    { id: 'bamboo', name: 'Bamboo' },
    { id: 'plugin', name: 'Third party plugin' }
  ], []);

  const licenseExpiryOptions = useMemo(() => [
    { id: '', name: '默认有效期' },
    { id: '1', name: '1年' },
    { id: '3', name: '3年' },
    { id: '5', name: '5年' },
    { id: '10', name: '10年' },
    { id: 'custom', name: '自定义' }
  ], []);

  const licenseTypes = useMemo(() => [
    { id: '2', name: 'Data Center 许可证' },
    { id: '1', name: 'Server 许可证' }
  ], []);

  const steps = useMemo(() => ['基础配置', '许可证信息', '服务器配置', '完成'], []);

  const [formData, setFormData] = useState({
    service: '',
    configName: '',
    mail: '',
    name: '',
    url: '',
    serverid: '',
    licenseType: '2',
    pluginKey: '',
    agentPath: '/opt/.atlassian-agent/atlassian-agent.jar',
    expiryOption: '',
    customExpiry: ''
  });

  const [generatedLicense, setGeneratedLicense] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [quickFillText, setQuickFillText] = useState('');

  const quickFillResult = useMemo(
    () => parseLicenseInfoText(quickFillText),
    [quickFillText]
  );

  const expirySummary = useMemo(() => {
    if (formData.expiryOption === 'custom') {
      const customYears = formData.customExpiry.trim();

      return customYears
        ? {
            mode: '自定义期限',
            title: `${customYears} 年有效期`,
            description: '生成时将按当前填写的年限写入许可证参数。',
            source: '表单指定',
          }
        : {
            mode: '等待填写',
            title: '请输入自定义年限',
            description: '支持 1–970 之间的整数，填写后会同步更新此处摘要。',
            source: '待补充',
          };
    }

    if (formData.expiryOption) {
      return {
        mode: '固定期限',
        title: `${formData.expiryOption} 年有效期`,
        description: '生成时将按所选固定年限写入许可证参数。',
        source: '选项指定',
      };
    }

    return {
      mode: '默认规则',
      title: '沿用生成服务默认期限',
      description: '当前未指定年限，最终期限由许可证生成服务的默认规则决定。',
      source: '生成服务',
    };
  }, [formData.customExpiry, formData.expiryOption]);

  // 表单验证
  const [formValidation, setFormValidation] = useState({
    basicConfig: false,
    licenseInfo: false,
    serverConfig: false
  });

  const [isCompleted, setIsCompleted] = useState(false);

  // 使用 useCallback 优化验证函数
  const checkBasicConfig = useCallback((data) => {
    return data.agentPath && data.service;
  }, []);

  const checkLicenseInfo = useCallback((data) => {
    return data.configName && data.name && data.mail;
  }, []);

  const checkServerConfig = useCallback((data) => {
    const baseCheck = Boolean(data.url?.trim() && data.serverid?.trim() && data.licenseType);
    if (!baseCheck) return false;
    if (data.service === 'plugin') {
      return Boolean(data.pluginKey?.trim());
    }
    return true;
  }, []);

  // 处理历史信息选择
  const handleHistorySelect = useCallback((historyData) => {
    setFormData(prev => ({
      ...prev,
      name: historyData.name,
      mail: historyData.mail,
      url: historyData.url,
      serverid: historyData.serverid,
      licenseType: historyData.licenseType,
      configName: historyData.configName // 添加配置名称的处理
    }));
  }, []);

  // 优化验证状态更新，使用防抖减少频繁更新
  useEffect(() => {
    const timer = setTimeout(() => {
      const newValidation = {
        basicConfig: checkBasicConfig(formData),
        licenseInfo: checkLicenseInfo(formData),
        serverConfig: checkServerConfig(formData)
      };
      
      // 只有验证状态真正改变时才更新
      if (JSON.stringify(newValidation) !== JSON.stringify(formValidation)) {
        setFormValidation(newValidation);
      }
    }, 300); // 增加防抖时间到300ms
    
    return () => clearTimeout(timer);
  }, [formData, checkBasicConfig, checkLicenseInfo, checkServerConfig, formValidation]);

  // 更新步骤
  useEffect(() => {
    let newStep = 0;
    if (formValidation.basicConfig) newStep = 1;
    if (formValidation.basicConfig && formValidation.licenseInfo) newStep = 2;
    if (formValidation.basicConfig && formValidation.licenseInfo && formValidation.serverConfig && generatedLicense && isCompleted) {
      newStep = 3;
    }
    setCurrentStep(newStep);
  }, [formValidation, isCompleted, generatedLicense]);

  // 清理许可证内容，去除装饰性文本
  const cleanLicenseContent = useCallback((content) => {
    if (!content) return '';

    const lines = content.replace(/\r\n?/g, '\n').split('\n');
    const licenseMarkerIndex = lines.findIndex((line) =>
      line.toLowerCase().includes('your license code')
    );

    // 当前生成器会在该标记后输出许可证正文。以标记作为分界，避免把有效期说明复制进去。
    if (licenseMarkerIndex >= 0) {
      return lines.slice(licenseMarkerIndex + 1).join('\n').trim();
    }

    // 兼容没有正文标记的历史输出格式。
    return lines
      .filter((line) => {
        const trimmed = line.trim();

        return trimmed &&
          !trimmed.includes('====') &&
          !trimmed.includes('IT 智汇坊') &&
          !trimmed.includes('Atlassian 安装授权') &&
          !trimmed.includes('dqzboy.taobao.com') &&
          !trimmed.includes('更多技术服务') &&
          !trimmed.includes('设置许可证有效期') &&
          !trimmed.includes('到期日期') &&
          !trimmed.includes('Don\'t copy this line') &&
          !trimmed.toLowerCase().includes('your license code');
      })
      .join('\n')
      .trim();
  }, []);

  const generatedLicenseMeta = useMemo(() => {
    const cleanedContent = cleanLicenseContent(generatedLicense);
    const lineCount = cleanedContent
      ? cleanedContent.split(/\r?\n/).filter(line => line.trim()).length
      : 0;

    return {
      product: services.find(item => item.id === formData.service)?.name || '未识别产品',
      configName: formData.configName || '未命名配置',
      licenseType: licenseTypes.find(item => item.id === formData.licenseType)?.name || '未识别类型',
      expiry: expirySummary.title,
      characterCount: cleanedContent.length,
      lineCount,
    };
  }, [cleanLicenseContent, expirySummary.title, formData.configName, formData.licenseType, formData.service, generatedLicense, licenseTypes, services]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneratedLicense('');
    setIsLoading(true);
    setIsCompleted(false);
    
    try {
      if (!formData.service) {
        throw new Error('请选择服务');
      }
      
      if (formData.service === 'plugin' && !formData.pluginKey) {
        throw new Error('插件服务需要填写插件密钥');
      }

      if (formData.expiryOption === 'custom') {
        if (!formData.customExpiry) {
          throw new Error('请填写自定义有效期');
        }
        const customExpiry = formData.customExpiry.trim();
        if (!/^\d+$/.test(customExpiry)) {
          throw new Error('自定义有效期必须是数字，表示许可证的年数');
        }
      }

      const requestData = {
        ...formData,
        url: formData.url.startsWith('http') ? formData.url : `http://${formData.url}`
      };

      if (formData.expiryOption) {
        if (formData.expiryOption === 'custom') {
          requestData.expiry = formData.customExpiry.trim();
        } else {
          requestData.expiry = formData.expiryOption;
        }
      }

      const response = await api.post(ENDPOINTS.GENERATE_LICENSE, requestData);
      const data = response.data;
      
      if (data.success && data.license) {
        toast.success('许可证生成成功');
        setGeneratedLicense(data.license);
        setIsCompleted(true);
      } else {
        throw new Error(data.error || '生成许可证失败');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || '生成许可证失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 使用 useCallback 优化输入处理函数
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    if (name === 'service') {
      setIsCompleted(false);
      setGeneratedLicense('');
      setFormData(prev => ({
        ...prev,
        service: value,
        // 切换产品时保留已经粘贴识别或手动填写的许可证信息。
        // 插件密钥只对第三方插件生效，离开插件服务时再清空即可。
        pluginKey: value === 'plugin' ? prev.pluginKey : ''
      }));
      return;
    }
    
    if (name === 'customExpiry') {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue > 970) {
        setFormData(prev => ({ ...prev, [name]: '970' }));
        toast.info('有效期已自动设置为最大值970年');
        return;
      }
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const applyQuickFillText = useCallback((rawText, notify = true) => {
    const result = parseLicenseInfoText(rawText);

    if (result.recognizedCount === 0) {
      if (notify) toast.error('未识别到许可证信息，请检查粘贴格式');
      return result;
    }

    setFormData(prev => ({
      ...prev,
      ...result.values,
    }));
    setGeneratedLicense('');
    setIsCompleted(false);

    if (notify) {
      toast.success(`已识别并填入 ${result.recognizedCount} 项许可证信息`);
    }

    return result;
  }, []);

  const handleQuickFillPaste = useCallback((event) => {
    const pastedText = event.clipboardData.getData('text/plain');
    if (!pastedText) return;

    event.preventDefault();
    setQuickFillText(pastedText);
    applyQuickFillText(pastedText);
  }, [applyQuickFillText]);

  const handleQuickFillChange = useCallback((event) => {
    const nextText = event.target.value;
    setQuickFillText(nextText);

    if (nextText.trim()) {
      applyQuickFillText(nextText, false);
    }
  }, [applyQuickFillText]);

  const handleQuickFillClear = useCallback(() => {
    setQuickFillText('');
  }, []);

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-stack generator-page">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <PageHeader
            eyebrow="Operations / License issue"
            title="许可证生成器"
            description="选择产品并填写授权信息，系统将通过内置 Agent 生成许可证。"
            icon={Settings2}
            status="Java Agent 就绪"
            actions={<Badge variant="outline">4 步完成</Badge>}
          />
        </motion.div>

        <div className="generator-layout">
        {/* 步骤指示器 - 移动端优化 */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="workflow-strip generator-rail"
        >
          <Card>
            <CardContent className="p-4 sm:p-6">
            <div className="generator-rail__top">
              <div className="generator-rail__heading">
                <span>ISSUE WORKFLOW</span>
                <h2>生成进度</h2>
                <p>按顺序完成配置，由内置 Java Agent 生成许可证。</p>
              </div>
              <div className="generator-rail__steps">
                {steps.map((step, index) => (
                  <React.Fragment key={index}>
                    <div className="generator-rail__step">
                      <div className={cn('workflow-step', index <= currentStep && 'is-active')}>
                        {index < currentStep ? (
                          <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="generator-rail__step-copy">
                        <small>STEP {String(index + 1).padStart(2, '0')}</small>
                        {step}
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={cn('workflow-connector', index < currentStep && 'is-active')} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="generator-rail__summary">
              <span><small>当前产品</small><strong>{services.find((item) => item.id === formData.service)?.name || '尚未选择'}</strong></span>
              <span><small>许可证类型</small><strong>{licenseTypes.find((item) => item.id === formData.licenseType)?.name || '尚未选择'}</strong></span>
              <span><small>有效期</small><strong>{licenseExpiryOptions.find((item) => item.id === formData.expiryOption)?.name || '默认期限'}</strong></span>
            </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* 表单区域 */}
        <form onSubmit={handleSubmit} className="license-form generator-main">
          <div className="license-form__canvas">
            <div className="license-form__canvas-header">
              <div>
                <span>LICENSE PARAMETERS</span>
                <h2>授权参数</h2>
                <p>所有字段会在提交前进行校验，带必填标记的内容不能为空。</p>
              </div>
              <Badge variant="outline">安全签发</Badge>
            </div>

            <section className="license-quick-fill" aria-labelledby="license-quick-fill-title">
              <div className="license-quick-fill__header">
                <span className="license-quick-fill__icon" aria-hidden="true">
                  <ClipboardPaste />
                </span>
                <div className="license-quick-fill__heading">
                  <small>SMART PASTE</small>
                  <h3 id="license-quick-fill-title">粘贴并自动识别</h3>
                  <p>粘贴固定格式内容后，自动填入许可证信息和服务器配置。</p>
                </div>
                <span
                  className="license-quick-fill__status"
                  data-state={quickFillResult.recognizedCount > 0 ? 'detected' : 'idle'}
                  aria-live="polite"
                >
                  <Sparkles aria-hidden="true" />
                  {quickFillText
                    ? `已识别 ${quickFillResult.recognizedCount} / ${LICENSE_INFO_FIELDS.length} 项`
                    : '等待粘贴'}
                </span>
              </div>

              <div className="license-quick-fill__content">
                <div className="license-quick-fill__editor">
                  <textarea
                    value={quickFillText}
                    onChange={handleQuickFillChange}
                    onPaste={handleQuickFillPaste}
                    rows="5"
                    spellCheck="false"
                    aria-label="粘贴许可证信息"
                    placeholder={'1. 邮箱地址：…\n2. 授权用户名称：…\n3. 服务器访问URL：…\n4. Server ID：…'}
                  />
                  <div className="license-quick-fill__editor-footer">
                    <span>支持中英文冒号、编号列表及 Markdown URL</span>
                    {quickFillText && (
                      <button type="button" onClick={handleQuickFillClear}>
                        <RotateCcw aria-hidden="true" />
                        清空内容
                      </button>
                    )}
                  </div>
                </div>

                <div className="license-quick-fill__preview" aria-label="自动识别结果">
                  <div className="license-quick-fill__preview-title">
                    <span>识别结果</span>
                    <small>粘贴后自动写入对应字段</small>
                  </div>
                  <div className="license-quick-fill__fields">
                    {LICENSE_INFO_FIELDS.map((field) => {
                      const value = quickFillResult.values[field.key];

                      return (
                        <div
                          key={field.key}
                          className="license-quick-fill__field"
                          data-detected={Boolean(value)}
                        >
                          <span aria-hidden="true">{value ? <Check /> : '—'}</span>
                          <div>
                            <small>{field.label}</small>
                            <strong title={value || undefined}>{value || '等待识别'}</strong>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="license-quick-fill__apply"
                    disabled={quickFillResult.recognizedCount === 0}
                    onClick={() => applyQuickFillText(quickFillText)}
                  >
                    <ArrowDownToLine data-icon="inline-start" />
                    重新填入表单
                  </Button>
                </div>
              </div>
            </section>

            <div className="license-form__grid">
            {/* 基础配置 */}
            <AnimatedFormSection
              icon={Settings2}
              label="基础配置"
              description="确认 Agent 路径并选择需要签发许可证的产品。"
              step="STEP 01"
              delay={0.1}
              className="license-section--basic"
            >
              <FormField
                label="Agent JAR 路径"
                icon={FileKey}
                name="agentPath"
                value={formData.agentPath}
                onChange={handleInputChange}
                required
              />
              <SelectField
                label="选择服务"
                icon={Settings2}
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                required
              >
                <option value="">请选择服务</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </SelectField>
            </AnimatedFormSection>

            {/* 许可证信息 */}
            <AnimatedFormSection
              icon={User}
              label="许可证信息"
              description="填写配置标识与最终许可证持有者信息。"
              step="STEP 02"
              delay={0.15}
              className="license-section--identity"
            >
              {/* 为插件服务显示历史信息选择器 */}
              {formData.service === 'plugin' && (
                <div className="mb-4">
                  <HistorySelector 
                    onSelect={handleHistorySelect} 
                    service={formData.service}
                  />
                  <div className="form-inline-note">
                    <AlertCircle aria-hidden="true" />选择已有程序配置后，将自动填充授权名、邮箱、域名和许可证类型，并在配置名称后添加“_plugin”后缀；你只需输入插件密钥。
                  </div>
                </div>
              )}
              
              <FormField
                label="配置名称"
                name="configName"
                value={formData.configName}
                onChange={handleInputChange}
                icon={Settings2}
                placeholder="输入配置名称"
                required
              />
              <FormField
                label="授权名"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                icon={User}
                placeholder="输入授权名"
                required
              />
              <FormField
                label="邮箱"
                type="email"
                name="mail"
                value={formData.mail}
                onChange={handleInputChange}
                icon={Mail}
                placeholder="输入邮箱地址"
                required
              />
            </AnimatedFormSection>

            {/* 服务器配置 */}
            <AnimatedFormSection
              icon={Server}
              label="服务器配置"
              description="绑定访问地址、服务器 ID 与许可证类型。"
              step="STEP 03"
              delay={0.2}
              className={cn(
                'license-section--server',
                formData.expiryOption === 'custom' && 'license-section--custom-expiry-peer'
              )}
            >
              <FormField
                label="访问域名"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                icon={Globe}
                placeholder="http://example.com:8080"
                required
              />
              <SelectField
                label="许可证类型"
                icon={KeyRound}
                name="licenseType"
                value={formData.licenseType}
                onChange={handleInputChange}
                required
              >
                {licenseTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </SelectField>
              <FormField
                label="服务器ID"
                name="serverid"
                value={formData.serverid}
                onChange={handleInputChange}
                icon={Server}
                placeholder="输入服务器ID"
                required
              />
              {formData.service === 'plugin' && (
                <FormField
                  label="插件密钥"
                  name="pluginKey"
                  value={formData.pluginKey}
                  onChange={handleInputChange}
                  icon={KeyRound}
                  placeholder="输入插件密钥"
                  required
                />
              )}
            </AnimatedFormSection>

            {/* 有效期配置 */}
            <AnimatedFormSection
              icon={Calendar}
              label="有效期配置"
              description="选择默认期限，或设置自定义有效年限。"
              step="STEP 04"
              delay={0.25}
              className="license-section--expiry"
            >
              <SelectField
                label="许可证有效期"
                icon={Calendar}
                name="expiryOption"
                value={formData.expiryOption}
                onChange={handleInputChange}
              >
                {licenseExpiryOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </SelectField>
              {formData.expiryOption === 'custom' && (
                <FormField
                  label="自定义有效期 (年)"
                  type="number"
                  name="customExpiry"
                  value={formData.customExpiry}
                  onChange={handleInputChange}
                  icon={Calendar}
                  placeholder="输入年数 (最大970年)"
                  min="1"
                  max="970"
                  required
                />
              )}
              <div className="expiry-guidance" aria-live="polite">
                <div className="expiry-guidance__summary">
                  <span className="expiry-guidance__icon" aria-hidden="true">
                    <CalendarRange />
                  </span>
                  <div className="expiry-guidance__copy">
                    <small>{expirySummary.mode}</small>
                    <strong>{expirySummary.title}</strong>
                    <p>{expirySummary.description}</p>
                  </div>
                </div>

                <div className="expiry-guidance__timeline" aria-hidden="true">
                  <span className="is-active" />
                  <i />
                  <span className="is-active" />
                  <i />
                  <span />
                </div>

                <div className="expiry-guidance__facts">
                  <div>
                    <Calendar aria-hidden="true" />
                    <span><small>期限来源</small><strong>{expirySummary.source}</strong></span>
                  </div>
                  <div>
                    <Clock3 aria-hidden="true" />
                    <span><small>写入时机</small><strong>许可证生成时</strong></span>
                  </div>
                </div>

                <p className="expiry-guidance__note">
                  自定义期限支持 1–970 年；最终有效期以生成后的许可证内容为准。
                </p>
              </div>
            </AnimatedFormSection>
            </div>

            {/* 操作按钮 - 移动端优化 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="license-form__submit"
            >
              <div className="license-form__submit-copy">
                <strong>准备生成许可证</strong>
                <span>提交后将调用本地 Agent，生成结果会显示在页面下方。</span>
              </div>
              <Button
                type="submit"
                size="lg"
                className="w-full sm:w-auto"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin" data-icon="inline-start" />
                    <span>生成中...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 data-icon="inline-start" />
                    <span>生成许可证</span>
                  </>
                )}
              </Button>
            </motion.div>
          </div>
        </form>

        {/* 许可证展示 */}
        <AnimatePresence>
          {generatedLicense && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="generator-output"
            >
              <AnimatedFormSection 
                icon={FileKey} 
                label="许可证交付"
                description="核对本次签发信息，并复制或下载生成结果。"
                tooltip="您可以复制许可证内容或下载为文件"
                className="license-result-card"
              >
                <div className="license-result">
                  <section className="license-result__summary" aria-label="许可证签发摘要">
                    <div className="license-result__status">
                      <span className="license-result__status-icon" aria-hidden="true">
                        <CheckCircle2 />
                      </span>
                      <div>
                        <span className="license-result__eyebrow">ISSUANCE COMPLETE</span>
                        <strong>许可证已成功生成</strong>
                        <p>请在部署前核对产品、配置名称与许可证类型。</p>
                      </div>
                    </div>

                    <dl className="license-result__facts">
                      <div>
                        <dt>产品</dt>
                        <dd title={generatedLicenseMeta.product}>{generatedLicenseMeta.product}</dd>
                      </div>
                      <div>
                        <dt>配置名称</dt>
                        <dd title={generatedLicenseMeta.configName}>{generatedLicenseMeta.configName}</dd>
                      </div>
                      <div>
                        <dt>许可证类型</dt>
                        <dd title={generatedLicenseMeta.licenseType}>{generatedLicenseMeta.licenseType}</dd>
                      </div>
                      <div>
                        <dt>有效期</dt>
                        <dd title={generatedLicenseMeta.expiry}>{generatedLicenseMeta.expiry}</dd>
                      </div>
                    </dl>
                  </section>

                  <section className="license-result__document" aria-label="许可证正文">
                    <header className="license-result__document-bar">
                      <div className="license-result__document-title">
                        <span aria-hidden="true"><KeyRound /></span>
                        <div>
                          <strong>许可证原始输出</strong>
                          <small>LICENSE PAYLOAD</small>
                        </div>
                      </div>
                      <div className="license-result__document-meta" aria-label="许可证正文统计">
                        <span>{generatedLicenseMeta.lineCount} 行</span>
                        <span>{generatedLicenseMeta.characterCount.toLocaleString('zh-CN')} 字符</span>
                        <Badge variant="outline">只读</Badge>
                      </div>
                    </header>

                    <div className="license-output" tabIndex="0">
                      <pre><code>{generatedLicense.trim()}</code></pre>
                    </div>
                  </section>

                  <footer className="license-result__actions">
                    <div className="license-result__action-copy">
                      <strong>许可证已准备就绪</strong>
                      <span>复制时自动去除说明与装饰文字；下载文件保留完整原始输出。</span>
                    </div>

                    <div className="license-result__buttons">
                      <Button
                        type="button"
                        onClick={() => {
                          const cleanedContent = cleanLicenseContent(generatedLicense);
                          navigator.clipboard.writeText(cleanedContent);
                          toast.success('已复制许可证正文');
                        }}
                        variant="outline"
                      >
                        <Copy data-icon="inline-start" />
                        复制许可证
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          const blob = new Blob([generatedLicense], { type: 'text/plain' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `license-${Date.now()}.txt`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        }}
                      >
                        <Download data-icon="inline-start" />
                        下载文件
                      </Button>
                    </div>
                  </footer>
                </div>
              </AnimatedFormSection>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default LicenseGenerator;
